/**
 * Auth Context - Refactored with Secure Storage
 * Provides authentication state and methods throughout the app
 * 
 * SECURITY IMPROVEMENTS:
 * - Removed password storage in cookies
 * - Migrated auth data to localStorage
 * - Reduced cookies to minimal essential data
 * - Added secure storage utility
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useCookies } from 'react-cookie';
import authService from '../services/authService';
import { updateAuthHeader } from '../../../shared/services/apiClient';
import logger from '../../../shared/utils/logger';
import { API_BASE_URL } from '../../../shared/utils/constants';
import storage, { 
  saveAuthData, 
  getAuthData, 
  clearAuthData,
  migrateFromCookies,
  getCookiesToRemove,
  saveActiveTab,
  getActiveTab,
} from '../../../shared/utils/storage';
import { isTokenExpired } from '../../../shared/utils/security';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Minimal cookies - only for backward compatibility during migration
  const [cookies, setCookie, removeCookie] = useCookies(['Rememberme']);
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  /**
   * Remove deprecated and insecure cookies
   */
  const cleanupDeprecatedCookies = useCallback(() => {
    const cookiesToRemove = getCookiesToRemove();
    cookiesToRemove.forEach(cookieName => {
      try {
        removeCookie(cookieName, { path: '/' });
      } catch (e) {
        // Ignore errors when removing cookies
      }
    });
  }, [removeCookie]);

  // Initialize auth state from secure storage
  useEffect(() => {
    const initializeAuth = () => {
      try {
        // First, try to migrate from old cookie-based storage
        migrateFromCookies(cookies);
        
        // Get auth data from secure storage
        const { token, userData } = getAuthData();
        
        if (token && userData?.id && !isTokenExpired(token)) {
          updateAuthHeader(token);
          setUser({
            id: userData.id,
            userName: userData.userName,
            employeeName: userData.employeeName,
            email: userData.email,
            roleName: userData.roleName,
            roleID: userData.roleID,
            departmentid: userData.departmentid,
            companyID: userData.companyID,
            locationStatus: userData.locationStatus,
            privileges: userData.privileges || [],
            idleTime: userData.idleTime, // Include idle time
          });
          setIsAuthenticated(true);
          logger.info('Auth initialized from secure storage');
        } else if (token && isTokenExpired(token)) {
          logger.warn('Stored auth token is expired. Clearing auth data.');
          updateAuthHeader(null);
          clearAuthData();
          setUser(null);
          setIsAuthenticated(false);
        }
        
        // Clean up deprecated cookies (security risk)
        cleanupDeprecatedCookies();
        
      } catch (error) {
        logger.error('Error initializing auth:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep AuthContext state in sync when API client detects unauthorized session
  useEffect(() => {
    const handleUnauthorized = () => {
      logger.warn('Received auth:unauthorized event. Resetting session state.');
      updateAuthHeader(null);
      clearAuthData();
      cleanupDeprecatedCookies();
      removeCookie('Rememberme', { path: '/' });
      setUser(null);
      setIsAuthenticated(false);
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [cleanupDeprecatedCookies, removeCookie]);

  // Send best-effort logout when tab/window is closing to capture logout_time in reports.
  useEffect(() => {
    const sendCloseLogoutBeacon = () => {
      try {
        if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
          return;
        }

        const { token, userData } = getAuthData();
        const employeeId = user?.id || userData?.id;
        if (!employeeId || !token) return;

        const endpoint = `${API_BASE_URL}/Login/SetLogoutTimeOnClose?Id=${encodeURIComponent(employeeId)}&token=${encodeURIComponent(token)}`;
        navigator.sendBeacon(endpoint);
      } catch (error) {
        logger.debug('Close-logout beacon failed:', error);
      }
    };

    window.addEventListener('pagehide', sendCloseLogoutBeacon);
    window.addEventListener('beforeunload', sendCloseLogoutBeacon);

    return () => {
      window.removeEventListener('pagehide', sendCloseLogoutBeacon);
      window.removeEventListener('beforeunload', sendCloseLogoutBeacon);
    };
  }, [user?.id]);

  // Listen for idle time configuration updates
  useEffect(() => {
    const handleIdleTimeUpdate = (event) => {
      const { idleMinutes } = event.detail;
      logger.info('Updating user idle time:', idleMinutes);
      
      setUser(prevUser => {
        if (!prevUser) return prevUser;
        
        const updatedUser = {
          ...prevUser,
          idleTime: idleMinutes
        };
        
        // Update stored user data as well
        const { token, userData, rememberMe } = getAuthData();
        if (userData) {
          saveAuthData(token, updatedUser, rememberMe);
        }
        
        return updatedUser;
      });
    };

    window.addEventListener('settings:idleTimeUpdated', handleIdleTimeUpdate);
    
    return () => {
      window.removeEventListener('settings:idleTimeUpdated', handleIdleTimeUpdate);
    };
  }, []);

  /**
   * Login user - SECURE: No passwords stored
   */
  const login = useCallback(async (credentials) => {
    try {
      const response = await authService.authenticate(credentials);
      
      logger.info('Login response received');
      
      // Get token
      const token = response.token;
      updateAuthHeader(token);
      
      // Handle both property name formats (with and without underscores)
      const userID = response.userID || response.id;
      const userName = response.userName || response.user_Name;
      const employeeName = response.employeeName || response.employee_Name;
      const emailID = response.emailID || response.email;
      const roleName = response.roleName || response.role_Name;
      const roleID = response.roleID || response.role_ID;
      const companyID = response.companyID || response.company_ID;
      const departmentids = response.departmentids || response.departmentIds;
      
      // Parse privileges
      const privilegesArray = response.userPrivileges 
        ? (typeof response.userPrivileges === 'string' 
            ? response.userPrivileges.split(',') 
            : response.userPrivileges)
        : [];
      
      // Create user object
      const userData = {
        id: userID,
        userName: userName,
        employeeName: employeeName,
        email: emailID,
        roleName: roleName,
        roleID: roleID,
        companyID: companyID,
        departmentid: departmentids,
        locationStatus: response.locationStatus,
        privileges: privilegesArray,
        idleTime: response.idletime,
        associateTitle: response.associateTitle,
        profilePicture: response.profilePicture,
        roleCount: response.roleCount,
      };
      
      // ✅ SECURE: Save auth data to localStorage (NO PASSWORDS!)
      saveAuthData(token, userData, credentials.rememberMeflag);
      
      // Only store remember-me flag in cookies (minimal, non-sensitive)
      if (credentials.rememberMeflag) {
        setCookie('Rememberme', true, { path: '/', maxAge: 604800 }); // 7 days
      }
      
      // Set state
      setUser(userData);
      setIsAuthenticated(true);
      
      // Clean up any old insecure cookies
      cleanupDeprecatedCookies();
      
      logger.info('Login successful - auth data stored securely');
      return response;
      
    } catch (error) {
      logger.error('Login failed:', error);
      throw error;
    }
  }, [setCookie, cleanupDeprecatedCookies]);

  /**
   * Logout user - clears all auth data
   */
  const logout = useCallback(async () => {
    try {
      // Call logout API if user exists
      if (user?.id) {
        await authService.setLogoutTime(Number(user.id));
      }
    } catch (error) {
      logger.error('Logout API call failed:', error);
    } finally {
      // Clear auth header
      updateAuthHeader(null);
      
      // ✅ Clear secure storage
      clearAuthData();
      
      // Clean up all cookies
      cleanupDeprecatedCookies();
      removeCookie('Rememberme', { path: '/' });
      
      // Clear state
      setUser(null);
      setIsAuthenticated(false);
      
      logger.info('Logout complete - all auth data cleared');
    }
  }, [user?.id, removeCookie, cleanupDeprecatedCookies]);

  /**
   * Save active tab preference (replaces cookie-based tab storage)
   */
  const saveTabPreference = useCallback((page, tabId) => {
    saveActiveTab(page, tabId);
  }, []);

  /**
   * Get active tab preference
   */
  const getTabPreference = useCallback((page, defaultTab = 0) => {
    return getActiveTab(page, defaultTab);
  }, []);

  /**
   * Update user idle time
   * @param {number} idleMinutes - New idle time in minutes
   */
  const updateUserIdleTime = useCallback((idleMinutes) => {
    setUser(prevUser => {
      if (!prevUser) return prevUser;
      
      const updatedUser = {
        ...prevUser,
        idleTime: idleMinutes
      };
      
      // Update stored user data as well
      const { token, rememberMe } = getAuthData();
      if (token) {
        saveAuthData(token, updatedUser, rememberMe);
      }
      
      logger.info('User idle time updated:', idleMinutes);
      return updatedUser;
    });
  }, []);

  /**
   * Get user data - compatibility layer for old cookie-based access
   * @deprecated Use `user` state directly instead
   */
  const getUserData = useCallback(() => {
    return getAuthData().userData;
  }, []);

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    updateUserIdleTime, // Add the new method
    // Tab preferences (replaces cookie-based storage)
    saveTabPreference,
    getTabPreference,
    // Compatibility layer (will be removed in future)
    cookies: {
      // Provide read-only access to essential data from user state
      userID: user?.id,
      userName: user?.userName,
      employeeName: user?.employeeName,
      emailID: user?.email,
      roleName: user?.roleName,
      companyID: user?.companyID,
      departmentid: user?.departmentid,
      UserPrivileges: user?.privileges,
      userPrivileges: user?.privileges,
      idleTime: user?.idleTime,
      // Tab preferences
      DashboardActiveTabCookies: getActiveTab('dashboard', ''),
      ApplicationConfigActiveTab: getActiveTab('applicationConfig', ''),
    },
    setCookie: (key, value, options) => {
      // Redirect tab settings to localStorage
      if (key.includes('ActiveTab') || key.includes('Cookies')) {
        const page = key.replace('ActiveTabCookies', '').replace('Cookies', '').toLowerCase();
        saveActiveTab(page, value);
      } else {
        // For other cookies, still allow setting (will be deprecated)
        logger.warn(`setCookie called for "${key}" - consider using storage utility instead`);
        setCookie(key, value, options);
      }
    },
    removeCookie,
    getUserData,
    // Storage utility access
    storage,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
